import { calculateNetSalary } from './calculate-finnish-net-salary';

/**
 * Take-home pay for a monthly gross, for the salary page's "käteen" figures and pay check.
 *
 * The full calculator is too heavy to ship to the browser just for this, so the server
 * precomputes a grid (every 50 € from 1 000 € to 9 000 €, national-average municipal tax, no
 * church tax, same defaults as /veroprosenttilaskuri without a municipality) and the client
 * interpolates. Linear interpolation between 50 € steps is within a couple of euros of the
 * exact figure because the tax curve is piecewise linear at that scale.
 */
export interface NetPayGrid {
  start: number;
  step: number;
  values: number[];
}

const GRID_START = 1000;
const GRID_END = 9000;
const GRID_STEP = 50;

export function netMonthlyFor(grossMonthly: number): number {
  return Math.round(calculateNetSalary({ grossMonthly }).netMonthly);
}

export function buildNetPayGrid(): NetPayGrid {
  const values: number[] = [];
  for (let g = GRID_START; g <= GRID_END; g += GRID_STEP) values.push(netMonthlyFor(g));
  return { start: GRID_START, step: GRID_STEP, values };
}

export function netFromGrid(grid: NetPayGrid, grossMonthly: number): number {
  const { start, step, values } = grid;
  if (values.length === 0 || !(grossMonthly > 0)) return 0;
  const pos = (grossMonthly - start) / step;
  if (pos <= 0) return Math.round(values[0] * (grossMonthly / start));
  const last = values.length - 1;
  if (pos >= last) {
    // Beyond the grid, extend with the last step's marginal keep rate.
    const slope = (values[last] - values[last - 1]) / step;
    return Math.round(values[last] + (grossMonthly - (start + last * step)) * slope);
  }
  const i = Math.floor(pos);
  const frac = pos - i;
  return Math.round(values[i] + (values[i + 1] - values[i]) * frac);
}
